// src/services/Master/PaketDetailTryout.js

import axios from 'axios';
import Cookies from 'js-cookie';

const BASE_URL_BE = process.env.REACT_APP_BASE_URL_BE; // Pastikan variabel env ini diatur

const getAuthToken = () => Cookies.get('access_token');

const getAuthHeaders = () => ({
  'Authorization': `Bearer ${getAuthToken()}`
});

/**
 * Mengambil daftar detail paket tryout dari API.
 * Mendukung paginasi dan filter.
 * @param {object} options - Opsi untuk request.
 * @param {number} [options.page=1] - Nomor halaman saat ini.
 * @param {number} [options.per_page=10] - Jumlah item per halaman.
 * @param {object} [options.filters={}] - Objek filter (misal: { id_paket: 1, is_premium: "1" }).
 * @param {boolean} [options.disable_pagination=false] - Jika true, ambil semua data tanpa paginasi.
 * @returns {Promise<object>} Objek berisi data, metadata, dan status.
 */
export const fetchPaketDetailTryout = async (options = {}) => {
  try {
    const params = new URLSearchParams();

    if (options.disable_pagination) {
      params.append('disable_pagination', 'true');
    } else {
      params.append('page', options.page || 1);
      params.append('per_page', options.per_page || 10);
    }

    if (options.filters) {
      if (options.filters.id_paket) {
        params.append('id_paket', options.filters.id_paket);
      }
      if (options.filters.durasi_menit_min) {
        params.append('durasi_menit_min', options.filters.durasi_menit_min);
      }
      if (options.filters.durasi_menit_max) {
        params.append('durasi_menit_max', options.filters.durasi_menit_max);
      }
      // is_premium bisa string "0"/"1" atau boolean, sesuaikan dengan backend Anda
      if (options.filters.is_premium !== undefined && options.filters.is_premium !== null && options.filters.is_premium !== '') {
        params.append('is_premium', options.filters.is_premium);
      }
    }

    const response = await axios.get(`${BASE_URL_BE}/paket_detail_tryout?${params.toString()}`, {
      headers: getAuthHeaders()
    });

    const meta = response.data.meta || {};
    const calculated_last_page = options.disable_pagination ? 1 : (Math.ceil(meta.total / meta.per_page) || 1);
    const currentPage = options.disable_pagination ? 1 : (meta.page || (options.page || 1));
    const currentPerPage = options.disable_pagination ? (meta.total || response.data.data.length || (options.per_page || 10)) : (meta.per_page || (options.per_page || 10));

    return {
      success: response.data.success,
      data: response.data.data || [],
      message: response.data.message || "",
      code: response.data.code || 200,
      meta: {
        page: currentPage,
        per_page: currentPerPage,
        total: meta.total || (options.disable_pagination ? response.data.data.length : 0),
        last_page: calculated_last_page
      }
    };
  } catch (error) {
    console.error("Error fetching Paket Detail Tryout:", error);
    throw {
      success: false,
      message: error.response?.data?.message || "Gagal memuat detail paket tryout.",
      errors: error.response?.data?.errors,
      code: error.response?.status
    };
  }
};

/**
 * Mengambil satu detail paket tryout berdasarkan ID Paket.
 * @param {number} idPaket - ID dari paket yang terkait.
 * @returns {Promise<object>} Objek berisi data detail paket tryout.
 */
export const fetchPaketDetailTryoutByPaketId = async (idPaket) => {
  try {
    const response = await axios.get(`${BASE_URL_BE}/paket_detail_tryout/paket/${idPaket}`, {
      headers: getAuthHeaders()
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching Paket Detail Tryout by Paket ID ${idPaket}:`, error);
    throw {
      success: false,
      message: error.response?.data?.message || `Gagal memuat detail paket tryout untuk ID Paket ${idPaket}.`,
      errors: error.response?.data?.errors,
      code: error.response?.status
    };
  }
};

/**
 * Menambahkan detail paket tryout baru.
 * @param {object} data - Data detail paket tryout yang akan ditambahkan.
 * @returns {Promise<object>} Respons dari API.
 */
export const createPaketDetailTryout = async (data) => {
  try {
    const response = await axios.post(`${BASE_URL_BE}/paket_detail_tryout`, data, {
      headers: getAuthHeaders() // Tidak perlu 'multipart/form-data' jika data JSON
    });
    return response.data;
  } catch (error) {
    console.error("Error creating Paket Detail Tryout:", error);
    throw {
      success: false,
      message: error.response?.data?.message || "Gagal membuat detail paket tryout.",
      errors: error.response?.data?.errors,
      code: error.response?.status
    };
  }
};

/**
 * Memperbarui detail paket tryout yang sudah ada.
 * @param {number} idDetailTryout - ID dari detail tryout yang akan diperbarui.
 * @param {object} data - Data detail paket tryout yang diperbarui.
 * @returns {Promise<object>} Respons dari API.
 */
export const updatePaketDetailTryout = async (idDetailTryout, data) => {
  try {
    const response = await axios.put(`${BASE_URL_BE}/paket_detail_tryout/${idDetailTryout}`, data, {
      headers: getAuthHeaders() // Tidak perlu 'multipart/form-data' jika data JSON
    });
    return response.data;
  } catch (error) {
    console.error(`Error updating Paket Detail Tryout ${idDetailTryout}:`, error);
    throw {
      success: false,
      message: error.response?.data?.message || `Gagal memperbarui detail paket tryout ${idDetailTryout}.`,
      errors: error.response?.data?.errors,
      code: error.response?.status
    };
  }
};

/**
 * Menghapus detail paket tryout.
 * @param {number} idDetailTryout - ID dari detail tryout yang akan dihapus.
 * @returns {Promise<object>} Respons dari API.
 */
export const deletePaketDetailTryout = async (idDetailTryout) => {
  try {
    const response = await axios.delete(`${BASE_URL_BE}/paket_detail_tryout/${idDetailTryout}`, {
      headers: getAuthHeaders()
    });
    return response.data;
  } catch (error) {
    console.error(`Error deleting Paket Detail Tryout ${idDetailTryout}:`, error);
    throw {
      success: false,
      message: error.response?.data?.message || `Gagal menghapus detail paket tryout ${idDetailTryout}.`,
      errors: error.response?.data?.errors,
      code: error.response?.status
    };
  }
};
